/**
 * Convert a given number of seconds to a string in the format "hh:mm:ss".
 * @param value - The number of seconds to convert.
 * @returns A formatted string in the format "hh:mm:ss".
 */
export const convertSecondsToHMS = (value: number): string => {
  const sec: number = parseInt(value.toString(), 10);
  const hours: number = Math.floor(sec / 3600);
  const minutes: number = Math.floor((sec - hours * 3600) / 60);
  const seconds: number = sec - hours * 3600 - minutes * 60;

  const padZero = (num: number): string => (num < 10 ? `0${num}` : num.toString());

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
};

/**
 * Returns the date as a string that is a specified number of days ago from the given date or the current date if no date is provided.
 * @param {Date | string} [date=new Date()] - The date from which the calculation is based. Defaults to the current date.
 * @param {number} days - The number of days ago from the given date.
 * @returns {string} - The date as a string in the ISO format.
 */
export const getDateNumberDaysAgo = (days: number, date: Date | string = new Date()) => {
  return new Date(new Date(date).setDate(new Date(date).getDate() - days)).toISOString();
};

/**
 * Returns the date that is a specified number of days
 * @param {Date} [date=new Date()] - The date from which the calculation is based. Defaults to the current date.
 * @param {number} days - The number of days to be added.
 * @returns {Date} - The date.
 */
export const addDaysToDate = (days: number, data = new Date()): Date => {
  return new Date(new Date(data).setDate(new Date(data).getDate() + days));
};

export const getIntegrationFormattedDate = (item: string | undefined): string => {
  const months = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May.',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sep.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];

  if (!item || item.length === 0) {
    return '-';
  }

  const day = new Date(item).getDate();
  const monthIndex = new Date(item).getMonth();
  const year = new Date(item).getFullYear();

  return `${year}.${months[monthIndex]}${day < 10 ? '0' + day : day}.`;
};

export const getDifferenceInDays = (
  from: string | null | undefined,
  to?: string | null | undefined
) => {
  let now = new Date().getTime();

  if (to) {
    now = new Date(to).getTime();
  }

  if (from) {
    const ends_at = new Date(from).getTime();
    const diffTime = Math.abs(ends_at - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }
};

type TimeMeasure = 'days' | 'hours' | 'minutes' | 'seconds';
/**
 * Returns the difference between two dates in the given measure.
 * @param {TimeMeasure} measure - The measure in which the difference is returned.
 * @param {Date | string} from - The date from which the calculation is based.
 * @param {Date | string} to - The date to which the calculation is based. Defaults to the current date.
 * @returns {number} - The difference between two dates in the given measure.
 */

export const getDifference = (
  measure: TimeMeasure,
  from: Date | string,
  to?: Date | string
): number => {
  const fromDateTime = new Date(from).getTime();
  const toDateTime = to ? new Date(to).getTime() : new Date().getTime();

  const diffTime = Math.abs(toDateTime - fromDateTime);
  switch (measure) {
    case 'days':
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    case 'hours':
      return Math.ceil(diffTime / (1000 * 60 * 60));
    case 'minutes':
      return Math.ceil(diffTime / (1000 * 60));
    case 'seconds':
      return Math.ceil(diffTime / 1000);
    default:
      return diffTime;
  }
};

/**
 * Rounds the given date to the nearest hour.
 * @param {Date | string} date - The date to be rounded.
 * @returns {string} - The rounded date in the ISO format.
 */
export const roundDateToNearestHour = (date: Date | string): string => {
  const d = new Date(date);
  d.setHours(d.getHours() + Math.round(d.getMinutes() / 60));
  d.setMinutes(0, 0, 0);
  return d.toISOString();
};

/**
 * Returns an ISO 8601 formatted date string without the timezone indicator (e.g., "2024-12-04T12:00:00.000").
 *
 * The function adjusts the input `Date` object to remove the timezone offset and formats it as an ISO string
 * without the trailing `Z` (UTC timezone indicator). If no date is provided, the current date and time are used.
 *
 * @param {Date | undefined} [date=new Date()] - The `Date` object to format. Defaults to the current date and time if undefined.
 * @returns {string} The ISO 8601 formatted date string without the timezone indicator.
 *
 * @example
 * // Example 1: Using a specific date
 * const inputDate = new Date('2024-12-04T15:30:00+02:00');
 * const result = dateWithoutTimezone(inputDate);
 * console.log(result); // "2024-12-04T13:30:00.000"
 *
 * @example
 * // Example 2: Using the current date
 * const result = dateWithoutTimezone();
 * console.log(result); // e.g., "2024-12-04T12:00:00.000" (current UTC-adjusted time)
 */
export const dateWithoutTimezone = (date: Date | undefined = new Date()): string => {
  const tzOffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
  const withoutTimezone = new Date(date.valueOf() - tzOffset)?.toISOString().slice(0, -1);
  return withoutTimezone;
};

/**
 * Extracts the date portion (`YYYY-MM-DD`) from an ISO 8601 formatted date string.
 *
 * @param {string} dateStr - The ISO 8601 formatted date string (e.g., "2024-12-04T15:30:00.000Z").
 * @returns {string} The date portion of the string in `YYYY-MM-DD` format.
 *
 * @example
 * const isoString = "2024-12-04T15:30:00.000Z";
 * const result = extractDateFromISOString(isoString);
 * console.log(result); // "2024-12-04"
 */
export const extractDateFromISOString = (dateStr: string): string => dateStr.substring(0, 10);

/**
 * Returns a new Date object representing the date 6 months prior to the given date.
 *
 * @param {Date} date - The input date.
 * @returns {Date} A new Date object 6 months prior to the input date.
 *
 * @example
 * const inputDate = new Date('2024-12-04');
 * const result = getDateSixMonthsEarlier(inputDate);
 * console.log(result); // Output: 2024-06-04T00:00:00.000Z
 */
export function getDateSixMonthsEarlier(date: Date): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() - 6); // Subtract 6 months
  return result;
}
